/**
 * Adam parameter address map — public `/track/...` paths → labActions writes.
 * Phase 0: mix, mute/solo, master FX wet/bypass, BPM.
 * Phase 2: synth `/track/{n}/instrument/preset/...` leaves.
 */

import {
  cloneDrumKit,
  clonePreset,
  cloneSamplerPreset,
  cloneWavetablePreset,
  type DrumKit,
  type FxNode,
  type InstrumentSlot,
  type SamplerPreset,
  type SynthPreset,
  type WavetablePreset,
} from "../../../../src/lib/audio";
import { FX_PARAM_RANGES } from "../lib/fxMeta";
import { getLabActions } from "../labActions";
import { projectStore } from "../store/projectStore";
import { transportStore } from "../store/transportStore";
import { uiStore } from "../store/uiStore";
import { normalizeControlPath } from "./controlRegistry";
import {
  dottedPresetLeafFromSlashParts,
  getPresetLeaf,
  resolveInstrumentLeaf,
  setPresetLeaf,
} from "./instrumentPresetLeaves";

function fxParamRange(key: string): [number, number] {
  const range = FX_PARAM_RANGES[key];
  if (range) {
    return [range.min, range.max];
  }
  return [0, 1];
}

export type ParamAddressKind =
  | "trackMixVolume"
  | "trackMixPan"
  | "trackMute"
  | "trackSolo"
  | "masterFxWet"
  | "masterFxEnabled"
  | "masterFxBypass"
  | "trackFxWet"
  | "trackFxEnabled"
  | "masterFxParam"
  | "trackFxParam"
  | "transportBpm"
  | "instrumentPresetLeaf";

export type ParamAddress = {
  path: string;
  kind: ParamAddressKind;
  /** Parameter domain used as default binding outputRange. */
  range: [number, number];
  label: string;
  slot?: InstrumentSlot;
  fxIndex?: number;
  /** FX node params key when kind is masterFxParam / trackFxParam. */
  fxParamKey?: string;
  /** Dotted SynthPreset leaf when kind is instrumentPresetLeaf. */
  presetLeaf?: string;
};

export type ApplyParamOptions = {
  /** Inject for tests — defaults to getLabActions(). */
  actions?: ReturnType<typeof getLabActions>;
  /** Inject for tests — defaults to projectStore.getValue(). */
  getProject?: () => ReturnType<typeof projectStore.getValue>;
  /** Inject for tests — defaults to transportStore.getValue(). */
  getTransport?: () => Pick<ReturnType<typeof transportStore.getValue>, "bpm">;
  /** Inject for tests — defaults to uiStore.getValue(). */
  getUi?: () => Pick<ReturnType<typeof uiStore.getValue>, "fxBypass">;
};

/** Discrete on/off targets — Learn should default to toggle mode. */
const DISCRETE_PARAM_KINDS = new Set<ParamAddressKind>([
  "trackMute",
  "trackSolo",
  "masterFxBypass",
  "masterFxEnabled",
  "trackFxEnabled",
]);

export function isDiscreteParamKind(kind: ParamAddressKind): boolean {
  return DISCRETE_PARAM_KINDS.has(kind);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function parseIndex(token: string | undefined): number | null {
  if (!token || !/^\d+$/.test(token)) {
    return null;
  }
  return Number(token);
}

/**
 * Resolve a public control path to a writable Adam parameter descriptor.
 * Returns null when the path is unknown / malformed.
 */
export function resolveParamAddress(path: string): ParamAddress | null {
  const normalized = normalizeControlPath(path);
  if (!normalized) {
    return null;
  }
  const parts = normalized.split("/").filter(Boolean);

  if (parts[0] === "transport" && parts[1] === "bpm" && parts.length === 2) {
    return {
      path: normalized,
      kind: "transportBpm",
      range: [40, 300],
      label: "BPM",
    };
  }

  if (parts[0] === "master" && parts[1] === "fxBypass" && parts.length === 2) {
    return {
      path: normalized,
      kind: "masterFxBypass",
      range: [0, 1],
      label: "Master FX bypass",
    };
  }

  if (parts[0] === "master" && parts[1] === "fx") {
    const fxIndex = parseIndex(parts[2]);
    if (fxIndex == null || fxIndex < 0) {
      return null;
    }
    if (parts[3] === "wet" && parts.length === 4) {
      return {
        path: normalized,
        kind: "masterFxWet",
        range: [0, 1],
        label: `Master FX ${fxIndex + 1} wet`,
        fxIndex,
      };
    }
    if (parts[3] === "enabled" && parts.length === 4) {
      return {
        path: normalized,
        kind: "masterFxEnabled",
        range: [0, 1],
        label: `Master FX ${fxIndex + 1} enable`,
        fxIndex,
      };
    }
    if (parts[3] === "params" && parts.length === 5 && parts[4]) {
      const key = parts[4];
      return {
        path: normalized,
        kind: "masterFxParam",
        range: fxParamRange(key),
        label: `Master FX ${fxIndex + 1} ${key}`,
        fxIndex,
        fxParamKey: key,
      };
    }
    return null;
  }

  if (parts[0] === "track") {
    const slot = parseIndex(parts[1]);
    if (slot == null || slot < 0) {
      return null;
    }
    const s = slot as InstrumentSlot;

    if (parts[2] === "mix" && parts[3] === "volume" && parts.length === 4) {
      return {
        path: normalized,
        kind: "trackMixVolume",
        range: [0, 1],
        label: `I${slot + 1} volume`,
        slot: s,
      };
    }
    if (parts[2] === "mix" && parts[3] === "pan" && parts.length === 4) {
      return {
        path: normalized,
        kind: "trackMixPan",
        range: [-1, 1],
        label: `I${slot + 1} pan`,
        slot: s,
      };
    }
    if (parts[2] === "mute" && parts.length === 3) {
      return {
        path: normalized,
        kind: "trackMute",
        range: [0, 1],
        label: `I${slot + 1} mute`,
        slot: s,
      };
    }
    if (parts[2] === "solo" && parts.length === 3) {
      return {
        path: normalized,
        kind: "trackSolo",
        range: [0, 1],
        label: `I${slot + 1} solo`,
        slot: s,
      };
    }
    if (parts[2] === "fx") {
      const fxIndex = parseIndex(parts[3]);
      if (fxIndex == null || fxIndex < 0) {
        return null;
      }
      if (parts[4] === "wet" && parts.length === 5) {
        return {
          path: normalized,
          kind: "trackFxWet",
          range: [0, 1],
          label: `I${slot + 1} FX ${fxIndex + 1} wet`,
          slot: s,
          fxIndex,
        };
      }
      if (parts[4] === "enabled" && parts.length === 5) {
        return {
          path: normalized,
          kind: "trackFxEnabled",
          range: [0, 1],
          label: `I${slot + 1} FX ${fxIndex + 1} enable`,
          slot: s,
          fxIndex,
        };
      }
      if (parts[4] === "params" && parts.length === 6 && parts[5]) {
        const key = parts[5];
        return {
          path: normalized,
          kind: "trackFxParam",
          range: fxParamRange(key),
          label: `I${slot + 1} FX ${fxIndex + 1} ${key}`,
          slot: s,
          fxIndex,
          fxParamKey: key,
        };
      }
    }

    if (parts[2] === "instrument" && parts[3] === "preset" && parts.length >= 5) {
      const leaf = dottedPresetLeafFromSlashParts(parts.slice(4));
      if (!leaf) {
        return null;
      }
      const kind = projectStore.getValue().instruments[s]?.kind;
      const meta = resolveInstrumentLeaf(kind, leaf);
      if (!meta) {
        return null;
      }
      return {
        path: normalized,
        kind: "instrumentPresetLeaf",
        range: meta.range,
        label: `I${slot + 1} ${meta.label}`,
        slot: s,
        presetLeaf: leaf,
      };
    }
  }

  return null;
}

/** Catalog of Phase‑0 addresses for a given track count (for UI / docs / tests). */
export function listPhase0ParamAddresses(trackCount: number): ParamAddress[] {
  const n = Math.max(1, Math.floor(trackCount));
  const out: ParamAddress[] = [
    resolveParamAddress("/transport/bpm")!,
    resolveParamAddress("/master/fxBypass")!,
  ];
  for (let i = 0; i < 8; i++) {
    const wet = resolveParamAddress(`/master/fx/${i}/wet`);
    const en = resolveParamAddress(`/master/fx/${i}/enabled`);
    if (wet) {
      out.push(wet);
    }
    if (en) {
      out.push(en);
    }
  }
  for (let slot = 0; slot < n; slot++) {
    for (const suffix of ["mix/volume", "mix/pan", "mute", "solo"] as const) {
      const addr = resolveParamAddress(`/track/${slot}/${suffix}`);
      if (addr) {
        out.push(addr);
      }
    }
  }
  return out;
}

/**
 * Read the live numeric value for a resolved parameter address.
 * Used by relative / step / toggle modes.
 */
export function readParamAddress(
  path: string,
  options: ApplyParamOptions = {},
): number | null {
  const addr = resolveParamAddress(path);
  if (!addr) {
    return null;
  }
  const project = options.getProject?.() ?? projectStore.getValue();

  switch (addr.kind) {
    case "transportBpm":
      return (options.getTransport?.() ?? transportStore.getValue()).bpm;
    case "trackMixVolume": {
      const mix = project.slotMix?.[addr.slot!];
      return typeof mix?.volume === "number" ? mix.volume : 1;
    }
    case "trackMixPan": {
      const mix = project.slotMix?.[addr.slot!];
      return typeof mix?.pan === "number" ? mix.pan : 0;
    }
    case "trackMute":
      return project.muted?.[addr.slot!] === true ? 1 : 0;
    case "trackSolo":
      return project.solo?.[addr.slot!] === true ? 1 : 0;
    case "masterFxBypass":
      return (options.getUi?.() ?? uiStore.getValue()).fxBypass ? 1 : 0;
    case "masterFxWet": {
      const node = project.fx?.[addr.fxIndex!];
      return typeof node?.wet === "number" ? node.wet : null;
    }
    case "masterFxEnabled": {
      const node = project.fx?.[addr.fxIndex!];
      return node ? (node.enabled !== false ? 1 : 0) : null;
    }
    case "masterFxParam": {
      const node = project.fx?.[addr.fxIndex!];
      const key = addr.fxParamKey;
      if (!node || !key) {
        return null;
      }
      const value = node.params?.[key];
      return typeof value === "number" && Number.isFinite(value) ? value : null;
    }
    case "trackFxWet": {
      const node = project.slotFx?.[addr.slot!]?.[addr.fxIndex!];
      return typeof node?.wet === "number" ? node.wet : null;
    }
    case "trackFxEnabled": {
      const node = project.slotFx?.[addr.slot!]?.[addr.fxIndex!];
      return node ? (node.enabled !== false ? 1 : 0) : null;
    }
    case "trackFxParam": {
      const node = project.slotFx?.[addr.slot!]?.[addr.fxIndex!];
      const key = addr.fxParamKey;
      if (!node || !key) {
        return null;
      }
      const value = node.params?.[key];
      return typeof value === "number" && Number.isFinite(value) ? value : null;
    }
    case "instrumentPresetLeaf": {
      const slot = addr.slot!;
      const leaf = addr.presetLeaf;
      if (!leaf) {
        return null;
      }
      const instrument = project.instruments?.[slot];
      if (!instrument) {
        return null;
      }
      if (instrument.kind === "synth") {
        const preset = project.presets[slot] ?? (instrument as { preset?: SynthPreset }).preset;
        if (!preset) {
          return null;
        }
        return getPresetLeaf(preset as unknown as Record<string, unknown>, leaf);
      }
      if (instrument.kind === "wavetable" || instrument.kind === "sampler") {
        return getPresetLeaf(
          instrument.preset as unknown as Record<string, unknown>,
          leaf,
        );
      }
      if (instrument.kind === "drums") {
        const padMatch = /^pads\.(\d+)\.(.+)$/.exec(leaf);
        if (!padMatch) {
          return null;
        }
        const pad = instrument.kit.pads[Number(padMatch[1])];
        if (!pad) {
          return null;
        }
        return getPresetLeaf(pad as unknown as Record<string, unknown>, padMatch[2]!);
      }
      if (instrument.kind === "sf2") {
        return getPresetLeaf(
          instrument.patch as unknown as Record<string, unknown>,
          leaf,
        );
      }
      return null;
    }
    default:
      return null;
  }
}

/**
 * Write a resolved parameter value through labActions (undoable where applicable).
 * Returns false if the path is unknown or actions are unavailable.
 */
export function applyParamAddress(
  path: string,
  value: number,
  options: ApplyParamOptions = {},
): boolean {
  if (!Number.isFinite(value)) {
    return false;
  }
  const addr = resolveParamAddress(path);
  if (!addr) {
    return false;
  }
  const actions = options.actions ?? getLabActions();
  if (!actions) {
    return false;
  }
  const project = options.getProject?.() ?? projectStore.getValue();
  const v = clamp(value, addr.range[0], addr.range[1]);

  switch (addr.kind) {
    case "transportBpm":
      actions.setBpm?.(Math.round(v));
      return true;
    case "trackMixVolume":
      actions.setSlotMix?.(addr.slot!, { volume: v });
      return true;
    case "trackMixPan":
      actions.setSlotMix?.(addr.slot!, { pan: v });
      return true;
    case "trackMute": {
      const want = v >= 0.5;
      const muted = project.muted[addr.slot!] === true;
      if (want !== muted) {
        actions.toggleMute?.(addr.slot!);
      }
      return true;
    }
    case "trackSolo": {
      const want = v >= 0.5;
      const solo = project.solo[addr.slot!] === true;
      if (want !== solo) {
        actions.toggleSolo?.(addr.slot!);
      }
      return true;
    }
    case "masterFxBypass":
      actions.setFxBypass?.(v >= 0.5);
      return true;
    case "masterFxWet":
    case "masterFxEnabled":
    case "masterFxParam": {
      const fxIndex = addr.fxIndex!;
      const stack = structuredClone(project.fx) as FxNode[];
      const node = stack[fxIndex];
      if (!node) {
        return false;
      }
      if (addr.kind === "masterFxWet") {
        node.wet = v;
      } else if (addr.kind === "masterFxEnabled") {
        node.enabled = v >= 0.5;
      } else {
        const key = addr.fxParamKey;
        if (!key) {
          return false;
        }
        node.params = { ...node.params, [key]: v };
      }
      actions.applyFxStack?.(stack);
      return true;
    }
    case "trackFxWet":
    case "trackFxEnabled":
    case "trackFxParam": {
      const fxIndex = addr.fxIndex!;
      const slot = addr.slot!;
      const stack = structuredClone(project.slotFx[slot] ?? []) as FxNode[];
      const node = stack[fxIndex];
      if (!node) {
        return false;
      }
      if (addr.kind === "trackFxWet") {
        node.wet = v;
      } else if (addr.kind === "trackFxEnabled") {
        node.enabled = v >= 0.5;
      } else {
        const key = addr.fxParamKey;
        if (!key) {
          return false;
        }
        node.params = { ...node.params, [key]: v };
      }
      actions.applySlotFxStack?.(slot, stack);
      return true;
    }
    case "instrumentPresetLeaf": {
      const slot = addr.slot!;
      const leaf = addr.presetLeaf;
      if (!leaf) {
        return false;
      }
      const instrument = project.instruments[slot];
      if (!instrument) {
        return false;
      }
      const meta = resolveInstrumentLeaf(instrument.kind, leaf);
      if (!meta) {
        return false;
      }
      const clampedLeaf = clamp(value, meta.range[0], meta.range[1]);

      if (instrument.kind === "synth") {
        const base = project.presets[slot];
        if (!base) {
          return false;
        }
        const next = clonePreset(base);
        const routeDepth = /^routes\.(\d+)\.depth$/.exec(leaf);
        if (routeDepth) {
          const index = Number(routeDepth[1]);
          if (!next.routes[index]) {
            return false;
          }
          next.routes = next.routes.map((entry, i) =>
            i === index ? { ...entry, depth: clampedLeaf } : entry,
          );
        } else {
          setPresetLeaf(next as unknown as Record<string, unknown>, leaf, clampedLeaf);
          if (leaf === "glideMs" && clampedLeaf <= 0) {
            delete next.glideMs;
          }
        }
        actions.updateInstrumentPreset?.(slot, next as SynthPreset);
        return true;
      }

      if (instrument.kind === "wavetable") {
        const next = cloneWavetablePreset(instrument.preset);
        const routeDepth = /^routes\.(\d+)\.depth$/.exec(leaf);
        if (routeDepth) {
          const index = Number(routeDepth[1]);
          if (!next.routes[index]) {
            return false;
          }
          next.routes = next.routes.map((entry, i) =>
            i === index ? { ...entry, depth: clampedLeaf } : entry,
          );
        } else {
          setPresetLeaf(next as unknown as Record<string, unknown>, leaf, clampedLeaf);
          if (leaf === "glideMs" && clampedLeaf <= 0) {
            delete (next as { glideMs?: number }).glideMs;
          }
        }
        actions.updateInstrumentWavetablePreset?.(slot, next as WavetablePreset);
        return true;
      }

      if (instrument.kind === "sampler") {
        const next = cloneSamplerPreset(instrument.preset);
        const routeDepth = /^routes\.(\d+)\.depth$/.exec(leaf);
        if (routeDepth) {
          const index = Number(routeDepth[1]);
          if (!next.routes[index]) {
            return false;
          }
          next.routes = next.routes.map((entry, i) =>
            i === index ? { ...entry, depth: clampedLeaf } : entry,
          );
        } else {
          setPresetLeaf(next as unknown as Record<string, unknown>, leaf, clampedLeaf);
        }
        actions.updateInstrumentSamplerPreset?.(slot, next as SamplerPreset);
        return true;
      }

      if (instrument.kind === "drums") {
        const padMatch = /^pads\.(\d+)\.(.+)$/.exec(leaf);
        if (!padMatch) {
          return false;
        }
        const padIndex = Number(padMatch[1]);
        const padLeaf = padMatch[2]!;
        const kit = cloneDrumKit(instrument.kit);
        const pad = kit.pads[padIndex];
        if (!pad) {
          return false;
        }
        setPresetLeaf(pad as unknown as Record<string, unknown>, padLeaf, clampedLeaf);
        actions.updateInstrumentKit?.(slot, kit as DrumKit);
        return true;
      }

      if (instrument.kind === "sf2") {
        if (leaf !== "gain") {
          return false;
        }
        actions.updateInstrumentSf2Patch?.(slot, { ...instrument.patch, gain: clampedLeaf });
        return true;
      }

      return false;
    }
    default:
      return false;
  }
}
